import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { MAX_PAGE_SIZE } from "@contractorhub/shared";
import type {
  AdminInvoicesQueryInput,
  AdminPaymentsQueryInput,
  AdminExpensesQueryInput,
  GstReportQueryInput,
  AdminTimesheetsQuery,
  AssignmentsQuery,
  ChaseQueryInput,
  DashboardQuery,
  MyInvoicesQueryInput,
  MyTimesheetQuery,
  MyTimesheetsQuery,
  ReportsQueryInput,
} from "@contractorhub/shared";
import { apiFetch, apiFile, isTransient } from "./api";

/**
 * Every field optional: the server owns the defaults, and the query key is built
 * from whatever the URL actually carries. Requiring the defaulted fields here
 * would force the router to spell out `sort`/`dir`/`pageSize` on every link just
 * to restate what the API would have chosen anyway — and would put them in the
 * query key, splitting the cache between two identical requests.
 */
type AdminTimesheetFilters = Partial<AdminTimesheetsQuery>;

/**
 * Query keys and options in one place. Mutations invalidate these keys — the
 * SPA's replacement for the old app's `revalidatePath`.
 */
export const queryKeys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  myAssignments: ["my", "assignments"] as const,
  myTimesheet: (query: MyTimesheetQuery) => ["my", "timesheet", query] as const,
  myTimesheets: (query: MyTimesheetsQuery) => ["my", "timesheets", query] as const,
  adminDashboard: ["admin", "dashboard"] as const,
  // Under ["admin"], which every directory edit (invite, assign, deactivate)
  // already invalidates.
  directoryDashboard: ["admin", "directory-dashboard"] as const,
  // Under ["my", "timesheets"], which the editor invalidates on every save,
  // submit and recall, so the contractor lands back on a dashboard that agrees.
  myDashboard: ["my", "timesheets", "dashboard"] as const,
  adminTimesheets: (query: AdminTimesheetFilters) =>
    ["admin", "timesheets", query] as const,
  adminTimesheetDetail: (id: string) => ["admin", "timesheet", id] as const,
  myNotifications: ["my", "notifications"] as const,
  adminReports: (query: ReportsQueryInput) => ["admin", "reports", query] as const,
  adminChase: (query: ChaseQueryInput) => ["admin", "chase", query] as const,
  adminContractors: ["admin", "contractors"] as const,
  adminAdmins: ["admin", "admins"] as const,
  adminClients: ["admin", "clients"] as const,
  adminProjects: ["admin", "projects"] as const,
  projectPicker: ["admin", "project-picker"] as const,
  adminAssignments: (query: AssignmentsQuery) => ["admin", "assignments", query] as const,
  adminAssignmentRates: (id: string) => ["admin", "assignment-rates", id] as const,
  // Contractor invoices (CINV).
  myInvoices: (query: Partial<MyInvoicesQueryInput>) =>
    ["my", "invoices", query] as const,
  myInvoiceDetail: (id: string) => ["my", "invoice", id] as const,
  myInvoicePdf: (id: string, fileVersion: number) =>
    ["my", "invoice-pdf", id, fileVersion] as const,
  adminInvoices: (query: Partial<AdminInvoicesQueryInput>) =>
    ["admin", "invoices", query] as const,
  adminInvoiceDetail: (id: string) => ["admin", "invoice", id] as const,
  adminExpenses: (query: Partial<AdminExpensesQueryInput>) => ["admin", "expenses", query] as const,
  adminExpenseDetail: (id: string) => ["admin", "expense", id] as const,
  adminExpensePdf: (id: string, fileVersion: number) => ["admin", "expense-pdf", id, fileVersion] as const,
  // Under ["admin", "invoices"] so a tag or a decision refreshes the project page too.
  adminProjectDetail: (id: string) => ["admin", "invoices", "project", id] as const,
  // Under ["admin", "invoices"] so every invoice decision that invalidates the
  // inbox refreshes the payment history with it.
  adminPayments: (query: Partial<AdminPaymentsQueryInput>) =>
    ["admin", "invoices", "payments", query] as const,
  adminInvoicePdf: (id: string, fileVersion: number) =>
    ["admin", "invoice-pdf", id, fileVersion] as const,
  // Not under ["admin", "invoices"]: an intake is not an invoice yet, and the
  // inbox's every refresh should not re-ask for them.
  adminInvoiceIntakes: (ids: readonly string[] | null) =>
    ["admin", "invoice-intakes", ids ?? "pending"] as const,
  adminInvoiceMonths: (contractorId: string) => ["admin", "invoice-months", contractorId] as const,
};

export const meQuery = queryOptions({
  queryKey: queryKeys.me,
  queryFn: () => apiFetch("GET /api/me"),
  // Every route guard awaits this one, so what it does on failure decides what
  // the user sees. A 401 is the ordinary signed-out answer, not an error — retry
  // it and every anonymous page load pays for three round-trips. A *transient*
  // failure is worth retrying: without this the guard throws and a visitor whose
  // first request caught the database mid-resume meets an error boundary rather
  // than the login page.
  retry: (failures, error) => failures < 2 && isTransient(error),
  retryDelay: (attempt) => 1000 * 2 ** attempt,
  staleTime: 5 * 60 * 1000,
});

export const myAssignmentsQuery = queryOptions({
  queryKey: queryKeys.myAssignments,
  queryFn: () => apiFetch("GET /api/my/assignments"),
  staleTime: 5 * 60 * 1000,
});

export const myTimesheetQuery = (query: MyTimesheetQuery) =>
  queryOptions({
    queryKey: queryKeys.myTimesheet(query),
    queryFn: () => apiFetch("GET /api/my/timesheet", { query }),
  });

export const myTimesheetsQuery = (query: MyTimesheetsQuery) =>
  queryOptions({
    queryKey: queryKeys.myTimesheets(query),
    queryFn: () => apiFetch("GET /api/my/timesheets", { query }),
  });

/**
 * Keyed under `adminDashboard`, so the invoice and timesheet mutations that
 * invalidate that prefix refresh every range at once.
 */
export const adminDashboardQuery = (query: DashboardQuery = {}) =>
  queryOptions({
    queryKey: [...queryKeys.adminDashboard, query] as const,
    queryFn: () => apiFetch("GET /api/manage/dashboard", { query }),
  });

export const directoryDashboardQuery = queryOptions({
  queryKey: queryKeys.directoryDashboard,
  queryFn: () => apiFetch("GET /api/manage/directory-dashboard"),
});

export const myDashboardQuery = queryOptions({
  queryKey: queryKeys.myDashboard,
  queryFn: () => apiFetch("GET /api/my/dashboard"),
});

export const adminTimesheetsQuery = (query: AdminTimesheetFilters) =>
  queryOptions({
    queryKey: queryKeys.adminTimesheets(query),
    queryFn: () =>
      apiFetch("GET /api/manage/timesheets", {
        query: { ...query, pageSize: 25 },
      }),
  });

/** The seven entries of one sheet — fetched only when an admin opens it (DATA-7). */
export const adminTimesheetDetailQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.adminTimesheetDetail(id),
    queryFn: () => apiFetch("GET /api/manage/timesheets/{id}", { params: { id } }),
  });

/**
 * Ask for the server's whole first page, not its default 25.
 *
 * The page has no pagination control — it shows a ranked list with a "Show all"
 * toggle — so whatever the first page holds is all it can ever display. At the
 * default that silently stopped at 25, and "Show all 25" is a sentence about 29
 * contractors that is simply wrong. Requesting the cap keeps it bounded (DATA-7)
 * while making the toggle honest; the footnote on the page covers the remainder.
 *
 * The cache key stays the caller's query, as `adminTimesheetsQuery` does above:
 * the page size is how this screen reads the endpoint, not part of what was asked.
 */
/** Under ["admin", "invoices"]: an invoice or expense decision moves the GST too. */
export const adminGstReportQuery = (query: GstReportQueryInput) =>
  queryOptions({
    queryKey: ["admin", "invoices", "gst", query] as const,
    queryFn: () => apiFetch("GET /api/manage/reports/gst", { query }),
  });

export const adminReportsQuery = (query: ReportsQueryInput = {}) =>
  queryOptions({
    queryKey: queryKeys.adminReports(query),
    queryFn: () =>
      apiFetch("GET /api/manage/reports", {
        query: { ...query, pageSize: MAX_PAGE_SIZE },
      }),
  });

export const adminChaseQuery = (query: ChaseQueryInput = {}) =>
  queryOptions({
    queryKey: queryKeys.adminChase(query),
    queryFn: () => apiFetch("GET /api/manage/chase", { query }),
  });

export const adminContractorsQuery = queryOptions({
  queryKey: queryKeys.adminContractors,
  queryFn: () => apiFetch("GET /api/manage/contractors"),
});

export const adminAdminsQuery = queryOptions({
  queryKey: queryKeys.adminAdmins,
  queryFn: () => apiFetch("GET /api/manage/admins"),
});

export const adminClientsQuery = queryOptions({
  queryKey: queryKeys.adminClients,
  queryFn: () => apiFetch("GET /api/manage/clients"),
});

/** Projects to assign work to, with no money on them — a user admin reads it (AUTH-13). */
export const projectPickerQuery = queryOptions({
  queryKey: queryKeys.projectPicker,
  queryFn: () => apiFetch("GET /api/manage/project-picker"),
});

export const adminProjectsQuery = queryOptions({
  queryKey: queryKeys.adminProjects,
  queryFn: () => apiFetch("GET /api/manage/projects"),
});

export const adminAssignmentsQuery = (query: AssignmentsQuery) =>
  queryOptions({
    queryKey: queryKeys.adminAssignments(query),
    queryFn: () => apiFetch("GET /api/manage/assignments", { query }),
  });

/** An assignment's rate history (RATE-1) — loaded only when an admin opens it. */
export const adminAssignmentRatesQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.adminAssignmentRates(id),
    queryFn: () => apiFetch("GET /api/manage/assignments/{id}/rates", { params: { id } }),
  });

export const authConfigQuery = queryOptions({
  queryKey: queryKeys.authConfig,
  queryFn: () => apiFetch("GET /api/auth/config"),
  staleTime: Infinity,
});

export const myNotificationsQuery = queryOptions({
  queryKey: queryKeys.myNotifications,
  queryFn: () => apiFetch("GET /api/my/notifications"),
});

// --- Contractor invoices (CINV) ----------------------------------------------

/**
 * The contractor's whole list. Bounded by construction — one row per month — so
 * the server's cap is the page size, and the page never needs a pager.
 */
export const myInvoicesQuery = (query: Partial<MyInvoicesQueryInput> = {}) =>
  queryOptions({
    queryKey: queryKeys.myInvoices(query),
    queryFn: () =>
      apiFetch("GET /api/my/invoices", { query: { ...query, pageSize: MAX_PAGE_SIZE } }),
  });

export const myInvoiceDetailQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.myInvoiceDetail(id),
    queryFn: () => apiFetch("GET /api/my/invoices/{id}", { params: { id } }),
  });

export const adminProjectDetailQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.adminProjectDetail(id),
    queryFn: () => apiFetch("GET /api/manage/projects/{id}", { params: { id } }),
  });

export const adminExpensesQuery = (query: Partial<AdminExpensesQueryInput>) =>
  queryOptions({
    queryKey: queryKeys.adminExpenses(query),
    queryFn: () => apiFetch("GET /api/manage/expenses", { query: { ...query, pageSize: 25 } }),
  });

export const adminExpenseDetailQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.adminExpenseDetail(id),
    queryFn: () => apiFetch("GET /api/manage/expenses/{id}", { params: { id } }),
  });

/** The document, keyed on its version so a replaced file fetches afresh. */
/**
 * Everything an expense write can change: the list, a project page's spend, and
 * Payments — where every live expense is a payment (EXP-7). The last two live
 * under ["admin", "invoices"], so invalidating the expense list alone left
 * Payments showing an expense as it was until its cache went stale.
 */
export function invalidateExpenseViews(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["admin", "expenses"] }),
    queryClient.invalidateQueries({ queryKey: ["admin", "invoices", "project"] }),
    queryClient.invalidateQueries({ queryKey: ["admin", "invoices", "payments"] }),
  ]);
}

/**
 * Everything an invoice's filing moves (CINV-35): the inbox's chips and filters,
 * project pages and Payments — all under ["admin", "invoices"] — and the
 * dashboard, since a re-file re-runs the checks it counts. `ids` are invoices
 * changed from the list, whose cached pages would otherwise hold an old version.
 */
export function invalidateInvoiceFiling(
  queryClient: QueryClient,
  ids: readonly string[] = []
): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["admin", "invoices"] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard }),
    ...ids.map((id) => queryClient.invalidateQueries({ queryKey: queryKeys.adminInvoiceDetail(id) })),
  ]);
}

export const adminExpensePdfQuery = (id: string, fileVersion: number) =>
  queryOptions({
    queryKey: queryKeys.adminExpensePdf(id, fileVersion),
    queryFn: () => apiFile("GET /api/manage/expenses/{id}/file", { params: { id } }),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: (failures, error) => failures < 2 && isTransient(error),
  });

export const adminPaymentsQuery = (query: Partial<AdminPaymentsQueryInput>) =>
  queryOptions({
    queryKey: queryKeys.adminPayments(query),
    queryFn: () =>
      apiFetch("GET /api/manage/payments", { query: { ...query, pageSize: 25 } }),
  });

export const adminInvoicesQuery = (query: Partial<AdminInvoicesQueryInput>) =>
  queryOptions({
    queryKey: queryKeys.adminInvoices(query),
    queryFn: () =>
      apiFetch("GET /api/manage/invoices", { query: { ...query, pageSize: 25 } }),
  });

/**
 * One invoice in full. The detail page adds a bounded `refetchInterval` while
 * the document is still being read — see `useInvoiceDetail` — rather than
 * baking a poll into the options every consumer inherits.
 */
export const adminInvoiceDetailQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.adminInvoiceDetail(id),
    queryFn: () => apiFetch("GET /api/manage/invoices/{id}", { params: { id } }),
  });

/**
 * The PDF itself, as a Blob. Keyed on the file version so a re-upload naturally
 * fetches the new document; the bytes never go stale on their own, so they are
 * kept for as long as the page holds them.
 */
export const adminInvoicePdfQuery = (id: string, fileVersion: number) =>
  queryOptions({
    queryKey: queryKeys.adminInvoicePdf(id, fileVersion),
    queryFn: () => apiFile("GET /api/manage/invoices/{id}/file", { params: { id } }),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: (failures, error) => failures < 2 && isTransient(error),
  });

export const myInvoicePdfQuery = (id: string, fileVersion: number) =>
  queryOptions({
    queryKey: queryKeys.myInvoicePdf(id, fileVersion),
    queryFn: () => apiFile("GET /api/my/invoices/{id}/file", { params: { id } }),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: (failures, error) => failures < 2 && isTransient(error),
  });

// --- Invoice intake (CINV-21) --------------------------------------------------

/** The intakes waiting to be filed, or one batch by id — what a review polls. */
export const adminInvoiceIntakesQuery = (ids: readonly string[] | null = null) =>
  queryOptions({
    queryKey: queryKeys.adminInvoiceIntakes(ids),
    queryFn: () =>
      apiFetch("GET /api/manage/invoice-intakes", { query: ids ? { ids: ids.join(",") } : {} }),
  });

/** How many intakes are waiting — the inbox's nudge, with no suggestions built. */
export const adminInvoiceIntakeCountQuery = queryOptions({
  queryKey: ["admin", "invoice-intakes", "count"] as const,
  queryFn: () => apiFetch("GET /api/manage/invoice-intakes", { query: { countOnly: "true" } }),
});

/** A contractor's invoiced months, so a month picker can say which are taken (CINV-1). */
export const adminInvoiceMonthsQuery = (contractorId: string) =>
  queryOptions({
    queryKey: queryKeys.adminInvoiceMonths(contractorId),
    queryFn: () => apiFetch("GET /api/manage/invoice-months", { query: { contractorId } }),
  });

/** A contractor's own drops (CINV-21): waiting ones, or one batch by id. */
export const myInvoiceIntakesQuery = (ids: readonly string[] | null = null) =>
  queryOptions({
    queryKey: ["my", "invoice-intakes", ids ?? "pending"] as const,
    queryFn: () => apiFetch("GET /api/my/invoice-intakes", { query: ids ? { ids: ids.join(",") } : {} }),
  });

export const myInvoiceIntakeCountQuery = queryOptions({
  queryKey: ["my", "invoice-intakes", "count"] as const,
  queryFn: () => apiFetch("GET /api/my/invoice-intakes", { query: { countOnly: "true" } }),
});
